import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    Image,
    PDFDownloadLink
} from '@react-pdf/renderer';
import ApiService, { API_BASE } from '../services/api';

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 10,
        fontFamily: 'Helvetica',
        backgroundColor: '#fafafa'
    },
    section: { marginBottom: 12 },

    // Header amélioré avec gradient effect simulé
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
        backgroundColor: '#ffffff',
        padding: 15,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        borderWidth: 1,
        borderColor: '#e8f4f8'
    },

    logo: {
        height: 45,
        width: 'auto',
        objectFit: 'contain',
        marginBottom: 4,
        alignSelf: 'flex-start'
    },

    // Titre avec style moderne
    title: {
        fontSize: 24,
        color: '#2563eb',
        fontWeight: 'bold',
        marginBottom: 4,
        letterSpacing: 1
    },

    companyName: {
        fontSize: 14,
        fontWeight: 'bold',
        lineHeight: 1.3,
        color: '#1f2937',
        marginBottom: 2
    },

    companyDetails: {
        fontSize: 9,
        lineHeight: 1.3,
        color: '#6b7280'
    },

    // Section client avec style card
    clientSection: {
        marginBottom: 15,
        backgroundColor: '#ffffff',
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e5e7eb'
    },

    clientTitle: {
        fontWeight: 'bold',
        marginBottom: 6,
        fontSize: 11,
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: 0.5
    },

    // Tableau moderne
    tableContainer: {
        backgroundColor: '#ffffff',
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb'
    },

    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f8fafc',
        padding: 8,
        borderBottomWidth: 1,
        borderColor: '#e2e8f0',
        fontWeight: 'bold'
    },

    tableRow: {
        flexDirection: 'row',
        padding: 8,
        borderBottomWidth: 0.5,
        borderColor: '#f1f5f9',
        backgroundColor: '#ffffff'
    },

    tableRowAlt: {
        flexDirection: 'row',
        padding: 8,
        borderBottomWidth: 0.5,
        borderColor: '#f1f5f9',
        backgroundColor: '#fafbfc'
    },

    colDescription: { flex: 3, paddingRight: 6 },
    colQty: { flex: 1, textAlign: 'center' },
    colPrice: { flex: 1, textAlign: 'right' },
    colTotal: { flex: 1, textAlign: 'right' },

    itemName: {
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: 1
    },

    itemDescription: {
        color: '#6b7280',
        fontSize: 9,
        lineHeight: 1.2
    },

    // Section totaux avec style moderne
    totalsSection: {
        alignSelf: 'flex-end',
        width: '50%',
        marginTop: 12,
        backgroundColor: '#ffffff',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb'
    },

    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
        paddingVertical: 1
    },

    totalRowFinal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 6,
        paddingTop: 6,
        borderTopWidth: 2,
        borderColor: '#2563eb',
        backgroundColor: '#eff6ff',
        padding: 6,
        borderRadius: 4
    },

    // Payment Section Styles
    paymentsSection: {
        marginTop: 15,
        backgroundColor: '#ffffff',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb'
    },

    paymentsHeader: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: 8,
        textAlign: 'center',
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderColor: '#e5e7eb'
    },

    paymentTableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f8fafc',
        padding: 6,
        borderBottomWidth: 1,
        borderColor: '#e2e8f0',
        fontWeight: 'bold',
        fontSize: 9
    },

    paymentTableRow: {
        flexDirection: 'row',
        padding: 6,
        borderBottomWidth: 0.5,
        borderColor: '#f1f5f9',
        fontSize: 9
    },

    paymentCol1: { flex: 2, paddingRight: 4 }, // Date
    paymentCol2: { flex: 2, paddingRight: 4 }, // Method
    paymentCol3: { flex: 3, paddingRight: 4 }, // Reference
    paymentCol4: { flex: 1, textAlign: 'right' }, // Amount

    paymentSummary: {
        marginTop: 8,
        padding: 8,
        backgroundColor: '#f0f9ff',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#bae6fd'
    },

    paymentSummaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2
    },

    paymentSummaryLabel: {
        fontSize: 10,
        color: '#0369a1',
        fontWeight: 'bold'
    },

    paymentSummaryValue: {
        fontSize: 10,
        color: '#0369a1',
        fontWeight: 'bold'
    },

    // Status badge styles
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'center',
        alignSelf: 'flex-start'
    },

    statusPaid: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },

    statusPartiallyPaid: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },

    statusOverdue: {
        backgroundColor: '#fee2e2',
        color: '#dc2626'
    },
    statusDirectDebit: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },

    // Notes avec style amélioré
    notes: {
        marginTop: 15,
        padding: 12,
        backgroundColor: '#fef3c7',
        borderRadius: 6,
        borderLeftWidth: 3,
        borderLeftColor: '#f59e0b'
    },

    notesTitle: {
        fontWeight: 'bold',
        marginBottom: 4,
        color: '#92400e'
    },

    notesText: {
        fontStyle: 'italic',
        color: '#78350f',
        lineHeight: 1.3
    },

    // Détails bancaires avec style carte
    bankDetails: {
        marginTop: 15,
        backgroundColor: '#ffffff',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb'
    },

    bankHeader: {
        marginBottom: 8,
        fontSize: 12,
        fontWeight: 'bold',
        color: '#1f2937',
        textAlign: 'center',
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderColor: '#e5e7eb'
    },

    bankContent: {
        flexDirection: 'row',
        justifyContent: 'space-between'
    },

    bankColumn: {
        width: '48%'
    },

    bankRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3,
        paddingVertical: 1
    },

    bankLabel: {
        fontWeight: 'bold',
        color: '#4b5563',
        fontSize: 9
    },

    bankValue: {
        textAlign: 'right',
        color: '#1f2937',
        fontSize: 9
    },

    // Style pour les remises
    discountRow: {
        backgroundColor: '#dcfce7',
        borderRadius: 4,
        padding: 6,
        marginVertical: 1
    },

    discountText: {
        color: '#166534',
        fontWeight: 'bold'
    },

    // Footer moderne
    footer: {
        marginTop: 20,
        textAlign: 'center',
        fontSize: 8,
        color: '#9ca3af',
        fontStyle: 'italic'
    }
});

const formatCurrency = (amount, currency) => `${currency}${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';

    // Invoice dates are calendar dates stored at UTC midnight. Local getters
    // shift them a day back in negative-offset timezones, and toLocaleDateString
    // follows the viewer's locale (M/D/YYYY in the US). Force UTC + DD/MM/YYYY.
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const InvoicePDF = ({ item, type, clients, companySettings }) => {
    const client = clients.find(c => c._id === item.clientId);
    const itemsTotal = item.items?.reduce((sum, i) => sum + (i.quantity * i.price), 0) || 0;
    const discount = itemsTotal * (item.discountPercentage || 0) / 100;
    const afterDiscount = itemsTotal - discount;
    const tax = companySettings.taxExempt ? 0 : afterDiscount * (companySettings.taxPercentage || 10) / 100;
    const total = afterDiscount + tax;

    // Payment calculations
    // Prefer recorded payments; fall back to the invoice's own running total,
    // since list views do not attach the payments array.
    const totalPaid = item.payments?.length
        ? item.payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0)
        : parseFloat(item.totalPaid || 0) || 0;
    const remaining = total - totalPaid;

    // Determine payment status
    const getPaymentStatus = () => {
        if (type !== 'invoice') return null;

        // The stored status wins: a paid or cancelled invoice must never render
        // as overdue just because its due date has passed.
        if (item.status === 'cancelled') {
            return { status: 'cancelled', label: 'CANCELLED', style: styles.statusOverdue };
        }
        if (item.status === 'paid' || (total > 0 && totalPaid >= total)) {
            return { status: 'paid', label: 'PAID', style: styles.statusPaid };
        }
        if (totalPaid > 0) {
            return { status: 'partial', label: 'PARTIALLY PAID', style: styles.statusPartiallyPaid };
        }
        // A Direct Debit is already collecting this; "overdue" would be wrong and alarming.
        if (item.status === 'payment-pending') {
            return { status: 'direct-debit', label: 'DIRECT DEBIT', style: styles.statusDirectDebit };
        }
        if (item.status === 'draft') return null;
        if (item.dueDate && new Date(item.dueDate) < new Date()) {
            return { status: 'overdue', label: 'OVERDUE', style: styles.statusOverdue };
        }
        return null;
    };

    const paymentStatus = getPaymentStatus();

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header moderne */}
                <View style={styles.header}>
                    <View>
                        {companySettings.logo && <Image src={companySettings.logo} style={styles.logo} />}
                        <Text style={styles.companyName}>{companySettings.name}</Text>
                        <Text style={styles.companyDetails}>{companySettings.address}</Text>
                        <Text style={styles.companyDetails}>{companySettings.phone} | {companySettings.email}</Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.title}>{type.toUpperCase()}</Text>
                        <Text style={{ fontSize: 11, color: '#6b7280', marginBottom: 1 }}>#{item.number}</Text>
                        <Text style={{ fontSize: 9, color: '#6b7280' }}>Date: {formatDate(item.date)}</Text>
                        {type === 'quote' && item.validUntil &&
                            <Text style={{ fontSize: 9, color: '#dc2626' }}>Valid Until: {formatDate(item.validUntil)}</Text>}
                        {type === 'invoice' && item.dueDate && paymentStatus &&
                            <Text
                                style={{
                                    fontSize: 9,
                                    color: paymentStatus.status === 'paid' ? '#6b7280' : '#dc2626',
                                }}
                            >
                                Due: {formatDate(item.dueDate)}
                            </Text>
                        }
                        {item.paymentTerms && <Text style={{ fontSize: 9, color: '#6b7280' }}>Terms: {item.paymentTerms}</Text>}
                        {item.poNumber && <Text style={{ fontSize: 9, color: '#6b7280' }}>PO: {item.poNumber}</Text>}

                        {/* Payment Status Badge */}
                        {paymentStatus && (
                            <View style={[styles.statusBadge, paymentStatus.style, { marginTop: 4 }]}>
                                <Text>{paymentStatus.label}</Text>
                            </View>
                        )}
                    </View>
                </View>

                {/* Section client moderne */}
                <View style={styles.clientSection}>
                    <Text style={styles.clientTitle}>{type === 'invoice' ? 'Bill To:' : 'Quote For:'}</Text>
                    <Text style={[styles.companyDetails, { fontSize: 11, fontWeight: 'bold', color: '#1f2937' }]}>{client?.name}</Text>
                    {client?.contactPerson && <Text style={styles.companyDetails}>Attn: {client.contactPerson}</Text>}
                    <Text style={styles.companyDetails}>{client?.address}</Text>
                    <Text style={styles.companyDetails}>{client?.email} {client?.phone ? `| ${client.phone}` : ''}</Text>
                    {client?.vatNumber && <Text style={styles.companyDetails}>VAT: {client.vatNumber}</Text>}
                </View>

                {/* Tableau moderne */}
                <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.colDescription}>Description</Text>
                        <Text style={styles.colQty}>Qty</Text>
                        <Text style={styles.colPrice}>Price</Text>
                        <Text style={styles.colTotal}>Total</Text>
                    </View>

                    {(item.items || []).map((i, idx) => (
                        <View style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt} key={idx}>
                            <View style={styles.colDescription}>
                                <Text style={styles.itemName}>{i.itemName || ''}</Text>
                                <Text style={styles.itemDescription}>{i.description}</Text>
                            </View>
                            <Text style={styles.colQty}>{i.quantity}</Text>
                            <Text style={styles.colPrice}>{formatCurrency(i.price, companySettings.currency)}</Text>
                            <Text style={styles.colTotal}>{formatCurrency(i.quantity * i.price, companySettings.currency)}</Text>
                        </View>
                    ))}
                </View>

                {/* Section totaux moderne */}
                <View style={styles.totalsSection}>
                    <View style={styles.totalRow}>
                        <Text style={{ color: '#6b7280' }}>Subtotal:</Text>
                        <Text style={{ fontWeight: 'bold' }}>{formatCurrency(itemsTotal, companySettings.currency)}</Text>
                    </View>
                    {item.discountPercentage > 0 && (
                        <View style={[styles.totalRow, styles.discountRow]}>
                            <Text style={styles.discountText}>Discount ({item.discountPercentage}%):</Text>
                            <Text style={styles.discountText}>-{formatCurrency(discount, companySettings.currency)}</Text>
                        </View>
                    )}
                    {!companySettings.taxExempt && (
                        <View style={styles.totalRow}>
                            <Text style={{ color: '#6b7280' }}>VAT ({companySettings.taxPercentage}%):</Text>
                            <Text>{formatCurrency(tax, companySettings.currency)}</Text>
                        </View>
                    )}
                    <View style={styles.totalRowFinal}>
                        <Text style={{ fontWeight: 'bold', fontSize: 12, color: '#1f2937' }}>Total:</Text>
                        <Text style={{ fontWeight: 'bold', fontSize: 12, color: '#2563eb' }}>{formatCurrency(total, companySettings.currency)}</Text>
                    </View>
                </View>

                {/* Payment Information Section - Only for invoices with payments */}
                {type === 'invoice' && item.payments && item.payments.length > 0 && (
                    <View style={styles.paymentsSection}>
                        <Text style={styles.paymentsHeader}>Payment History</Text>

                        {/* Payment Table Header */}
                        <View style={styles.paymentTableHeader}>
                            <Text style={styles.paymentCol1}>Date</Text>
                            <Text style={styles.paymentCol2}>Method</Text>
                            <Text style={styles.paymentCol3}>Reference</Text>
                            <Text style={styles.paymentCol4}>Amount</Text>
                        </View>

                        {/* Payment Rows */}
                        {item.payments.map((payment, idx) => (
                            <View style={styles.paymentTableRow} key={idx}>
                                <Text style={styles.paymentCol1}>{formatDate(payment.date)}</Text>
                                <Text style={styles.paymentCol2}>{payment.method || 'N/A'}</Text>
                                <Text style={styles.paymentCol3}>{payment.reference || '-'}</Text>
                                <Text style={styles.paymentCol4}>{formatCurrency(payment.amount, companySettings.currency)}</Text>
                            </View>
                        ))}

                        {/* Payment Summary */}
                        <View style={styles.paymentSummary}>
                            <View style={styles.paymentSummaryRow}>
                                <Text style={styles.paymentSummaryLabel}>Total Paid:</Text>
                                <Text style={styles.paymentSummaryValue}>{formatCurrency(totalPaid, companySettings.currency)}</Text>
                            </View>
                            {remaining > 0 && (
                                <View style={styles.paymentSummaryRow}>
                                    <Text style={[styles.paymentSummaryLabel, { color: '#dc2626' }]}>Balance Due:</Text>
                                    <Text style={[styles.paymentSummaryValue, { color: '#dc2626' }]}>{formatCurrency(remaining, companySettings.currency)}</Text>
                                </View>
                            )}
                            {remaining <= 0 && totalPaid > 0 && (
                                <View style={styles.paymentSummaryRow}>
                                    <Text style={[styles.paymentSummaryLabel, { color: '#166534' }]}>Status:</Text>
                                    <Text style={[styles.paymentSummaryValue, { color: '#166534' }]}>FULLY PAID</Text>
                                </View>
                            )}
                        </View>
                    </View>
                )}

                {/* Notes avec style amélioré */}
                {item.notes && (
                    <View style={styles.notes}>
                        <Text style={styles.notesTitle}>Notes:</Text>
                        <Text style={styles.notesText}>{item.notes}</Text>
                    </View>
                )}

                {/* Being collected by Direct Debit: say so rather than list bank details,
                    which would invite the client to pay a second time. */}
                {type === 'invoice' && remaining > 0 && item.status === 'payment-pending' && (
                    <View style={styles.bankDetails}>
                        <Text style={styles.bankHeader}>Paid by Direct Debit</Text>
                        <Text style={{ fontSize: 9 }}>
                            This invoice is being collected automatically by Direct Debit. Please do not send a separate payment.
                        </Text>
                    </View>
                )}

                {/* Détails bancaires modernes - Only show if balance is due */}
                {type === 'invoice' && remaining > 0 && item.status !== 'payment-pending' && (companySettings.bankName || companySettings.paymentInstructions) && (
                    <View style={styles.bankDetails}>
                        <Text style={styles.bankHeader}>Payment Information</Text>
                        <View style={styles.bankContent}>
                            <View style={styles.bankColumn}>
                                {companySettings.bankName && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Bank:</Text>
                                        <Text style={styles.bankValue}>{companySettings.bankName}</Text>
                                    </View>
                                )}
                                {companySettings.bankAddress && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Address:</Text>
                                        <Text style={styles.bankValue}>{companySettings.bankAddress}</Text>
                                    </View>
                                )}
                                {companySettings.sortCode && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Sort Code:</Text>
                                        <Text style={styles.bankValue}>{companySettings.sortCode}</Text>
                                    </View>
                                )}
                                {companySettings.accountNumber && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Account:</Text>
                                        <Text style={styles.bankValue}>{companySettings.accountNumber}</Text>
                                    </View>
                                )}
                            </View>
                            <View style={styles.bankColumn}>
                                {companySettings.iban && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>IBAN:</Text>
                                        <Text style={styles.bankValue}>{companySettings.iban}</Text>
                                    </View>
                                )}
                                {companySettings.bic && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>BIC:</Text>
                                        <Text style={styles.bankValue}>{companySettings.bic}</Text>
                                    </View>
                                )}
                                {companySettings.paymentMethods && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Methods:</Text>
                                        <Text style={styles.bankValue}>{companySettings.paymentMethods}</Text>
                                    </View>
                                )}
                                {companySettings.paymentInstructions && (
                                    <View style={styles.bankRow}>
                                        <Text style={styles.bankLabel}>Instructions:</Text>
                                        <Text style={styles.bankValue}>{companySettings.paymentInstructions}</Text>
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                )}

                {/* Footer pour les devis */}
                {type === 'quote' && item.validUntil && (
                    <View style={styles.footer}>
                        <Text>This quote is valid until {formatDate(item.validUntil)}</Text>
                        <Text>Thank you for your business!</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

// export const PDFLink = ({ item, type, clients, companySettings }) => (
//     <PDFDownloadLink
//         document={<InvoicePDF item={item} type={type} clients={clients} companySettings={companySettings} />}
//         fileName={`${type.toUpperCase()}-${item.number}.pdf`}
//         className='text-decoration-none dropdown-item'
//     >
//         {({ loading }) => loading ? (
//             <span>
//                 <i className="fas fa-download me-2"></i>
//                 Generating PDF...
//             </span>
//         ) : (
//             <span>
//                 <i className="fas fa-download me-2"></i>
//                 Download PDF
//             </span>
//         )}
//     </PDFDownloadLink>
// );

export const PDFLink = ({ item, type, className = "dropdown-item" }) => {
    let token = localStorage.getItem('auth_token') || localStorage.getItem('client_auth_token');
    
    const handleDownload = async () => {
        try {
            // Make the request to get the PDF buffer
            const response = await fetch(`${API_BASE}/pdfs?type=${type}&id=${item._id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`, // if you use auth
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to generate PDF');
            }

            // Convert response to blob
            const blob = await response.blob();
            
            // Create object URL from blob
            const url = window.URL.createObjectURL(blob);
            
            // Create temporary download link
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type.toUpperCase()}-${item.number || item._id}.pdf`;
            document.body.appendChild(a);
            a.click();
            
            // Cleanup
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Download failed:', error);
            // You might want to show a user-friendly error message
            alert('Failed to download PDF. Please try again.');
        }
    };

    return (
        <button
            className={className}
            onClick={handleDownload}
            type="button"
        >
            <i className="fas fa-download me-2"></i>
            Download PDF
        </button>
    );
};